import { useEffect, useRef, useState } from 'react';
import Icon from './Icon';
import { qrDataUrl } from '../lib/coach';
import { useApp } from '../lib/AppContext';

// ⑥ A full-screen QR invite to scan at the gym. It lasts 15 minutes and is replaced by a fresh one while the
// screen stays open; the screen checks every 3 s whether a student has joined.
export default function InviteQr({ invite, onClose }) {
  const { api } = useApp();
  const [current, setCurrent] = useState(invite);
  const [src, setSrc] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [joined, setJoined] = useState(null);
  const refreshing = useRef(false);

  useEffect(() => { qrDataUrl(current.url, { width: 720 }).then(setSrc).catch(() => setSrc(null)); }, [current.url]);

  // Keep the phone awake while the QR is up.
  useEffect(() => {
    let lock = null;
    navigator.wakeLock?.request('screen').then((l) => { lock = l; }).catch(() => {});
    return () => { lock?.release().catch(() => {}); };
  }, []);

  useEffect(() => {
    if (joined) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const poll = setInterval(() => {
      api.get(`/coach/invites/${current.invite_id}`).then((data) => {
        if (data.status === 'accepted') setJoined(data.accepted_by_name || 'Học viên');
      }).catch(() => {});
    }, 3000);
    return () => { clearInterval(tick); clearInterval(poll); };
  }, [api, current.invite_id, joined]);

  const left = Math.max(0, Math.round((new Date(current.expires_at) - now) / 1000));
  useEffect(() => {
    if (left > 0 || joined || refreshing.current) return;
    refreshing.current = true;
    api.post(`/coach/invites/${current.invite_id}/refresh_qr`).then(setCurrent).catch(() => {}).finally(() => { refreshing.current = false; });
  }, [left, joined, api, current.invite_id]);

  return (
    <div data-theme="macro-dark" role="dialog" aria-modal="true" aria-label="Mã QR lời mời"
      className="pt-safe pb-safe fixed inset-0 z-50 flex flex-col items-center justify-center gap-5 bg-[#101512] px-6 text-center text-base-content">
      <button type="button" className="btn btn-circle absolute right-4 top-[calc(env(safe-area-inset-top,0px)+16px)] size-11 border-0 bg-base-300"
        aria-label="Đóng" onClick={() => onClose(!!joined)}>
        <Icon name="x" className="size-5" />
      </button>
      {joined ? (
        <>
          <span className="grid size-20 place-items-center rounded-full bg-[#6fd3a0] text-[#15261f]"><Icon name="check" className="size-10" /></span>
          <p className="text-2xl font-extrabold">{joined} đã tham gia</p>
          <p className="text-sm text-muted">Pro đã được kích hoạt cho học viên.</p>
          <button type="button" className="btn btn-primary h-12 rounded-2xl border-0 px-8 font-bold" onClick={() => onClose(true)}>Xong</button>
        </>
      ) : (
        <>
          <p className="text-lg font-bold">Quét để nhận Pro từ bạn</p>
          <p className="text-sm text-muted">Mời: {current.nickname}</p>
          <div className="rounded-[24px] bg-white p-4">
            {src ? <img src={src} alt="Mã QR lời mời" className="size-[min(72vw,340px)]" /> : <div className="size-[min(72vw,340px)]" />}
          </div>
          <p className="tabular text-sm text-muted" aria-live="polite">
            Mã đổi sau {Math.floor(left / 60)}:{String(left % 60).padStart(2, '0')} · dùng được 1 lần
          </p>
        </>
      )}
    </div>
  );
}
